/* JDMuscle: "Pickup at Willow Grove: estimated by <date>" for variants that are NOT in stock at Willow Grove.
 * Liquid (snippets/jdm-willow-pickup-state.liquid) supplies the inputs only; every date is computed here, in the browser,
 * from today's America/New_York date, so a cached page never shows a stale date.
 * Priority: future custom.variant_eta  >  leadDays (chosen in Liquid = the same field/number the visible "takes N days to ship" line prints:
 * variant_lead_time / dropship_lead_time / product custom.lead_time for Special Order products)  >  no date (no fake date).
 * No lead time anywhere -> 2 calendar months from today's (order) date.
 * Lead times are CALENDAR days, because the visible line says "N days" (Leo 2026-10-24: use the same source/units); result rolled to a business day.
 * + RECEIVING_BUFFER business days for receiving/check-in at Willow (lead-time/default only; variant_eta from the ETA script already includes it). Weekends + JDMuscle closed days (central calendar, shop metafield
 * jdmuscle.holiday_calendar) are skipped; without the calendar, or outside its coverage, US federal holidays (observed) are skipped. */
(function (root) {
  'use strict';
  const THEME = String(root.Shopify?.theme?.id);
  const CFG = { TZ: 'America/New_York', CUTOFF_HOUR: 15, RECEIVING_BUFFER: 2, LONG_LEAD_DAYS: 90, DEFAULT_MONTHS: 2 };

  // ---- calendar helpers (dates are 'YYYY-MM-DD' strings; arithmetic on UTC midnight, DST-proof) ----
  const pad = (n) => String(n).padStart(2, '0');
  const ymd = (y, m, d) => `${y}-${pad(m)}-${pad(d)}`;
  const isYmd = (s) => { const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(s || '')); if (!m) return false; const d = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3])); return d.getUTCMonth() === +m[2] - 1 && d.getUTCDate() === +m[3]; };
  const toDate = (s) => { const [y, m, d] = s.split('-').map(Number); return new Date(Date.UTC(y, m - 1, d)); };
  const toYmd = (dt) => dt.toISOString().slice(0, 10);
  const addDays = (s, n) => toYmd(new Date(toDate(s).getTime() + n * 864e5));
  // Calendar months; day clamped to month end (Dec 31 + 2 months = Feb 28/29).
  const addMonths = (s, n) => { const [y, m, d] = s.split('-').map(Number); const t = new Date(Date.UTC(y, m - 1 + n, 1)); const dim = new Date(Date.UTC(t.getUTCFullYear(), t.getUTCMonth() + 1, 0)).getUTCDate(); return ymd(t.getUTCFullYear(), t.getUTCMonth() + 1, Math.min(d, dim)); };
  const diffDays = (a, b) => Math.round((toDate(b) - toDate(a)) / 864e5);
  // Same generator as gcr/inventory-rule/backend/holidays.mjs, but for any year (never runs out).
  const dow = (y, m, d) => new Date(Date.UTC(y, m - 1, d)).getUTCDay();
  const nth = (y, m, wd, n) => { let d = 1; while (dow(y, m, d) !== wd) d++; return d + 7 * (n - 1); };
  const last = (y, m, wd) => { let d = new Date(Date.UTC(y, m, 0)).getUTCDate(); while (dow(y, m, d) !== wd) d--; return d; };
  const observed = (y, m, d) => { const w = dow(y, m, d); return toYmd(new Date(Date.UTC(y, m - 1, d + (w === 6 ? -1 : w === 0 ? 1 : 0)))); };
  const holidayCache = new Map();
  function federalHolidays(y) {
    if (holidayCache.has(y)) return holidayCache.get(y);
    const out = [observed(y, 1, 1), ymd(y, 1, nth(y, 1, 1, 3)), ymd(y, 2, nth(y, 2, 1, 3)), ymd(y, 5, last(y, 5, 1)), observed(y, 6, 19), observed(y, 7, 4),
      ymd(y, 9, nth(y, 9, 1, 1)), ymd(y, 10, nth(y, 10, 1, 2)), observed(y, 11, 11), ymd(y, 11, nth(y, 11, 4, 4)), observed(y, 12, 25)];
    const nextNY = observed(y + 1, 1, 1); if (nextNY.startsWith(`${y}-`)) out.push(nextNY);
    const set = new Set(out); holidayCache.set(y, set); return set;
  }
  // ---- Central holiday calendar (shop metafield jdmuscle.holiday_calendar, rendered by Liquid into #jdm-holiday-calendar) ----
  // Warehouse receiving / pickup math uses jdmuscle_closed. Inside calendar.coverage, ONLY jdmuscle_closed (+ weekends) are closed;
  // outside coverage, or if the calendar is missing/invalid, fall back to the built-in federal-holiday generator above (old behavior).
  let calendar = null, calendarTried = false;
  function setCalendar(raw) {
    calendar = null; calendarTried = true;
    try {
      const c = typeof raw === 'string' ? JSON.parse(raw) : raw;
      if (!c || c.version !== 1 || !Array.isArray(c.jdmuscle_closed) || !c.coverage || !isYmd(c.coverage.from) || !isYmd(c.coverage.to) || c.coverage.from > c.coverage.to) return false;
      const closed = new Set();
      for (const h of c.jdmuscle_closed) { if (!h || !isYmd(h.date)) return false; closed.add(h.date); }
      calendar = { from: c.coverage.from, to: c.coverage.to, closed, version: c.version, updatedAt: c.updated_at || null };
      return true;
    } catch { return false; }
  }
  function loadCalendarFromDom() {
    if (calendarTried) return; calendarTried = true;
    const el = root.document && root.document.getElementById && root.document.getElementById('jdm-holiday-calendar');
    if (el) setCalendar(el.textContent);
  }
  const getCalendar = () => (calendar ? { from: calendar.from, to: calendar.to, closed: [...calendar.closed].sort(), version: calendar.version, updatedAt: calendar.updatedAt } : null);
  const isClosedDay = (s) => { loadCalendarFromDom(); return calendar && s >= calendar.from && s <= calendar.to ? calendar.closed.has(s) : federalHolidays(+s.slice(0, 4)).has(s); };
  const isBusinessDay = (s) => { const w = toDate(s).getUTCDay(); return w !== 0 && w !== 6 && !isClosedDay(s); };
  const nextBusinessDay = (s) => { let c = addDays(s, 1); while (!isBusinessDay(c)) c = addDays(c, 1); return c; };
  const rollToBusinessDay = (s) => (isBusinessDay(s) ? s : nextBusinessDay(s));
  const addBusinessDays = (s, n) => { let c = s; for (let i = 0; i < n; i++) c = nextBusinessDay(c); return c; };
  function localNow(instant, tz = CFG.TZ) {
    const o = {}; new Intl.DateTimeFormat('en-US', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' })
      .formatToParts(new Date(instant)).forEach((p) => { o[p.type] = p.value; });
    return { date: `${o.year}-${o.month}-${o.day}`, minutes: +o.hour * 60 + +o.minute };
  }
  const posInt = (v) => { const n = Number(v); return Number.isInteger(n) && n > 0 && n < 2000 ? n : null; };

  /** Pure: what the pickup line says. input = { state, eta, leadDays, leadSource, special }; now = epoch ms. */
  function pickupEstimate(input, now, cfg = CFG) {
    if (!input || input.state !== 'eta') return { kind: 'hidden' };
    const { date: today, minutes } = localNow(now, cfg.TZ);
    // Order is processed today if it is a business day and before the 3 PM ET cutoff; otherwise the next business day.
    const start = isBusinessDay(today) && minutes < cfg.CUTOFF_HOUR * 60 ? today : nextBusinessDay(today);
    let arrival = null, basis = null;
    if (isYmd(input.eta) && input.eta >= today) { arrival = rollToBusinessDay(input.eta); basis = 'variant_eta'; }   // today is not past (Leo 2026-10-24)
    else if (posInt(input.leadDays)) { arrival = rollToBusinessDay(addDays(start, posInt(input.leadDays))); basis = input.leadSource || 'lead'; }
    if (!arrival) { arrival = rollToBusinessDay(addMonths(today, cfg.DEFAULT_MONTHS)); basis = 'default_2_months'; }   // Leo 2026-10-24: no lead time anywhere = 2 calendar months from the order date
    // custom.variant_eta already includes the receiving buffer (ETA Apps Script, JdmEtaRules.gs), so +RECEIVING_BUFFER only for lead-time/default estimates.
    const date = basis === 'variant_eta' ? arrival : addBusinessDays(arrival, cfg.RECEIVING_BUFFER);
    if (diffDays(today, date) > cfg.LONG_LEAD_DAYS) return { kind: 'long', date, basis, special: !!input.special };
    return { kind: 'date', date, basis, special: !!input.special };
  }
  const fmtDay = (s) => new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', weekday: 'short', month: 'short', day: 'numeric' }).format(toDate(s));
  const fmtMonth = (s) => new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', month: 'long', year: 'numeric' }).format(toDate(s));
  /** Pure: estimate -> { line, note } (English customer copy). */
  function pickupWording(est) {
    const head = 'Pickup at Willow Grove:';
    switch (est.kind) {
      case 'date': return { line: `${head} estimated by ${fmtDay(est.date)}`, note: "Not in stock at our Willow Grove store yet. We'll email you when it's ready for pickup." };
      case 'long': return { line: `${head} ${est.special ? 'special order, ' : ''}estimated ${fmtMonth(est.date)}`, note: "Long lead time. We'll email you when it's ready for pickup." };
      case 'no-date': return { line: `${head} ${est.special ? 'special order. ' : ''}We'll email you when it's ready`, note: 'Call (866) 833-3536 for a current estimate.' };
      default: return null;
    }
  }

  /** Pure: full pickup line for a state source (Omega-free, 2026-10-24). */
  function pickupLine(src, now, cfg = CFG) {
    if (!src) return null;
    if (src.state === 'in_stock') return { line: 'Pick Up Available at Willow Grove, PA', note: 'Usually ready within 1 business hour, Mon–Fri 9 a.m.–5 p.m. ET.', kind: 'in_stock' };
    if (src.state === 'confirm') return { line: "Pickup at Willow Grove: we'll confirm stock by email before you visit.", note: 'Some units may already be on our shelf; wait for our ready-for-pickup email.', kind: 'confirm' };
    return pickupWording(pickupEstimate(src, now, cfg));
  }

  const api = { addMonths, pickupEstimate, pickupWording, pickupLine, rollToBusinessDay, nextBusinessDay, addDays, diffDays, isYmd, fmtDay, fmtMonth, federalHolidays, isBusinessDay, addBusinessDays, localNow, setCalendar, getCalendar, CFG };
  if (typeof module === 'object' && module.exports) module.exports = api;
  root.JdmPickupEta = api;
  if (!root.customElements || root.customElements.get('jdm-pickup-eta')) return;

  class JdmPickupEtaElement extends root.HTMLElement {
    connectedCallback() {
      if (this.ready || this.dataset.themeId !== THEME || String(root.Shopify?.theme?.id) !== THEME || !['main', 'unpublished'].includes(root.Shopify?.theme?.role)) return;
      this.scope = this.closest('product-info');
      this.form = root.document.getElementById(this.dataset.form);
      this.lineEl = this.querySelector('[data-pickup-eta-line]');
      this.noteEl = this.querySelector('[data-pickup-eta-note]');
      if (!this.scope || !this.form || !this.lineEl) return;
      this.ready = true; this.ticket = 0; this.cache = new Map();
      this.ctrl = new root.AbortController(); this.off = [];
      const initial = this.parse(this.querySelector('[data-pickup-eta-source]')?.textContent);
      if (initial) this.cache.set(initial.variantId, initial);
      const on = (t, n, f) => t.addEventListener(n, f, { signal: this.ctrl.signal });
      on(this.scope, 'change', (e) => { if (e.target.closest?.('variant-selects') || e.target.matches?.('[name="id"]')) this.update(); });
      on(root.document, 'visibilitychange', () => { if (root.document.visibilityState === 'visible') this.update(); });
      on(root, 'pageshow', () => this.update());
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS === 'object') {
        this.off.push(subscribe(PUB_SUB_EVENTS.variantChange, (msg) => { const d = msg?.data; if (d && String(d.sectionId) !== String(this.scope.sectionId || this.dataset.section)) return; this.update(d?.variant?.id); }));
      }
      // Re-render at the next 3 PM ET cutoff / midnight so a tab left open overnight never shows yesterday's date.
      this.timer = root.setInterval(() => this.render(), 60000);
      this.update();
    }
    disconnectedCallback() { this.ctrl?.abort(); this.off?.forEach((f) => f?.()); root.clearInterval(this.timer); this.ready = false; }
    parse(text) { try { const s = JSON.parse(text || 'null'); return s && s.v === 1 && /^\d+$/.test(String(s.variantId)) ? { ...s, variantId: String(s.variantId) } : null; } catch { return null; } }
    currentVariant(hint) { return String(hint || this.form.querySelector('[name="id"]')?.value || ''); }
    async update(hint) {
      const id = this.currentVariant(hint), ticket = ++this.ticket;
      this.variant = id;
      if (!/^\d+$/.test(id)) { this.show(null); return; }
      if (!this.cache.has(id)) {
        this.show(null);
        const src = await this.fetchSource(id);
        if (ticket !== this.ticket) return;          // a newer variant was selected meanwhile
        if (src) this.cache.set(id, src);
      }
      this.render();
    }
    async fetchSource(id) {
      try {
        let rootUrl = root.Shopify?.routes?.root || '/'; if (!rootUrl.endsWith('/')) rootUrl += '/';
        const url = new URL(`${rootUrl}variants/${id}/`, root.location.origin);
        url.searchParams.set('section_id', 'pickup-availability');
        if (root.Shopify?.theme?.role === 'unpublished') url.searchParams.set('preview_theme_id', THEME);
        const res = await root.fetch(url.toString(), { credentials: 'same-origin' });
        if (!res.ok) return null;
        const doc = new root.DOMParser().parseFromString(await res.text(), 'text/html');
        const src = this.parse(doc.querySelector('[data-pickup-eta-source]')?.textContent);
        return src && src.variantId === id ? src : null;
      } catch { return null; }
    }
    render() {
      if (!this.ready) return;
      // One deterministic pickup line from the server-rendered state (Omega removed 2026-10-24; jdm-native-pickup is hidden by CSS).
      const src = this.cache.get(this.variant);
      this.show(src ? pickupLine(src, Date.now()) : null);
      // Share the same server-rendered source with the delivery block (assets/jdm-delivery.js) so both lines always agree.
      if (src && src !== this.lastShared && typeof root.CustomEvent === 'function') { this.lastShared = src; this.scope.dispatchEvent(new root.CustomEvent('jdm:variant-source', { detail: src })); }
    }
    show(w) {
      if (!w) { this.hidden = true; this.lineEl.textContent = ''; if (this.noteEl) this.noteEl.textContent = ''; return; }
      if (this.lineEl.textContent !== w.line) this.lineEl.textContent = w.line;
      if (this.noteEl && this.noteEl.textContent !== w.note) this.noteEl.textContent = w.note;
      this.hidden = false;
    }
  }
  root.customElements.define('jdm-pickup-eta', JdmPickupEtaElement);
})(typeof window === 'object' ? window : globalThis);

/* JDM Upload 17 (2026-10-25): live support status on the Talk to an Enthusiast card.
 * Open = Mon-Fri 9:00 AM-6:00 PM America/New_York on JDMuscle business days. Reuses localNow() and isBusinessDay() above,
 * so it follows the same central holiday calendar (shop metafield jdmuscle.holiday_calendar, US federal fallback) as the
 * pickup line, the delivery window and the 3 PM countdown. Sets ONE class on the card (.is-open or .is-closed) and adds a
 * small dot span to each contact link; CSS shows green dots when open and grays the Call link (still a working tel: link)
 * when closed. Re-checks every minute and when the tab becomes visible. No JS = no class, no dots, all links normal. */
(function (root) {
  'use strict';
  const api = root.JdmPickupEta;
  const doc = root.document;
  if (!api || !doc || typeof api.localNow !== 'function' || typeof api.isBusinessDay !== 'function') return;
  const OPEN_MIN = 9 * 60, CLOSE_MIN = 18 * 60;
  const CLOSED_LABEL = 'Call (866) 833-3536 \u2014 open Mon\u2013Fri 9 AM\u20136 PM ET';
  const isSupportOpen = (now) => { const t = api.localNow(now); return api.isBusinessDay(t.date) && t.minutes >= OPEN_MIN && t.minutes < CLOSE_MIN; };
  api.isSupportOpen = isSupportOpen;
  let timer = null;
  function apply() {
    const cards = doc.querySelectorAll('.mst-card');
    if (!cards.length) return false;
    let open;
    try { open = isSupportOpen(Date.now()); } catch (e) { open = null; }
    cards.forEach((card) => {
      card.querySelectorAll('.mst-actions > a').forEach((a) => {
        if (a.querySelector(':scope > .jdm-online-dot')) return;
        const dot = doc.createElement('span');
        dot.className = 'jdm-online-dot';
        dot.setAttribute('aria-hidden', 'true');
        a.insertBefore(dot, a.firstChild);
      });
      card.classList.toggle('is-open', open === true);
      card.classList.toggle('is-closed', open === false);
      const phone = card.querySelector('.mst-phone');
      if (!phone) return;
      if (open === false) {
        phone.setAttribute('title', CLOSED_LABEL);
        phone.setAttribute('aria-label', CLOSED_LABEL);
        phone.dataset.jdmClosedLabel = '1';
      } else if (phone.dataset.jdmClosedLabel) {
        phone.removeAttribute('title');
        phone.removeAttribute('aria-label');
        delete phone.dataset.jdmClosedLabel;
      }
    });
    return true;
  }
  function start() {
    if (!apply() || timer) return;
    timer = root.setInterval(apply, 60000);
    doc.addEventListener('visibilitychange', () => { if (doc.visibilityState === 'visible') apply(); });
  }
  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', start, { once: true }); else start();
})(typeof window === 'object' ? window : globalThis);
